import { sqStateSynchronizer } from '@/core/core.stores';
import { PUSH_WORKBENCH } from '@/core/flux.service';
import { trackUserProfileSave } from '@/header/userProfileTracking.utilities';
import { switchLanguage } from '@/utilities/i18n.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import type { SystemPreferenceProperty } from '@/header/userProfileTracking.utilities';
import type { LocaleKey } from '@/utilities/i18n.constants';

/*
 * The System preferences tab saves each preference as soon as the user changes it. The workbench actions behind these
 * preferences save the workbench state themselves, so a failed save is handled by the state synchronizer rather than
 * reported here. The language is the exception, see saveLanguage().
 */

/** Applies a System preference through its workbench action, which saves it, and records the change */
export function saveSystemPreference(property: SystemPreferenceProperty, apply: () => void): void {
  apply();
  trackUserProfileSave('Edit System Preferences', [property], 'success');
}

/**
 * Switches the UI language and saves it. switchLanguage() also runs at startup to apply the stored language, so it does
 * not save the workbench state itself; this does, and reports a failure to the user.
 */
export async function saveLanguage(locale: LocaleKey): Promise<void> {
  await switchLanguage(locale);
  try {
    await sqStateSynchronizer.push(PUSH_WORKBENCH);
    trackUserProfileSave('Edit System Preferences', ['language'], 'success');
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    trackUserProfileSave('Edit System Preferences', ['language'], 'failure', error);
  }
}
